import React from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { HistoryEvent, EmployeeSummary } from './types';

interface HistoryTableProps {
  data: HistoryEvent[];
  selectedEmployee: string | null;
  employeeSummaries: EmployeeSummary[];
  onBackToSummary: () => void;
}

const getEventTypeBadge = (eventType: string) => {
  const eventStyles: Record<string, string> = {
    'sign-on': 'bg-green-100 text-green-800',
    'sign-off': 'bg-red-100 text-red-800',
    'continue': 'bg-yellow-100 text-yellow-800',
    'vacation-start': 'bg-blue-100 text-blue-800',
    'vacation-end': 'bg-purple-100 text-purple-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventStyles[eventType] || ''}`}>
      {eventType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
};

const HistoryTable: React.FC<HistoryTableProps> = ({
  data, selectedEmployee, employeeSummaries, onBackToSummary,
}) => {
  const columns: TableColumn[] = [
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (value: string) => (
        <div className="font-medium text-gray-900">{value}</div>
      ),
    },
    {
      key: 'eventType',
      label: 'Event Type',
      sortable: true,
      render: (value: string) => getEventTypeBadge(value),
    },
    {
      key: 'vesselName',
      label: 'Vessel',
      sortable: true,
      render: (value: string) => value || '-',
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      render: (value: string) => value || '-',
    },
    {
      key: 'eventDate',
      label: 'Event Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'notes',
      label: 'Notes',
      render: (value: string) => value || 'No notes',
    },
  ];

  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              Event History
              {selectedEmployee && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  - {employeeSummaries.find(emp => emp.employeeId === selectedEmployee)?.employeeName}
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-600">
              Showing {data.length} event{data.length !== 1 ? 's' : ''}
            </p>
          </div>
          {selectedEmployee && (
            <button
              onClick={onBackToSummary}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Back to Summary
            </button>
          )}
        </div>
      </div>
      <Table data={data} columns={columns} searchable={false} />
    </div>
  );
};

export default HistoryTable;
